import React from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { PhotoCarousel } from '@shared/components/PhotoCarousel';
import { SkeletonThumbnail } from '@shared/components/Skeleton';
import type { HotelTilePhotoData } from '../HotelTileCard.types';

const HORIZONTAL_ASPECT_RATIO = '1.1 / 1';
const VERTICAL_ASPECT_RATIO = '3 / 2';
const HORIZONTAL_IMAGE_MAX_WIDTH = 370;
const HORIZONTAL_IMAGE_MAX_HEIGHT = 315;
const VERTICAL_IMAGE_MAX_HEIGHT = 206;

interface HotelTilePhotosProps
  extends Readonly<{
    photos: ReadonlyArray<HotelTilePhotoData>;
    displayName: string;
    showIndex?: number;
    enableHorizontalView?: boolean;
    renderTagsOverlay?: () => React.ReactNode;
  }> {}

export const HotelTilePhotos: React.FC<HotelTilePhotosProps> = ({ photos, showIndex, enableHorizontalView = false, renderTagsOverlay }) => {
  return (
    <Box
      width="100%"
      position="relative"
      {...(enableHorizontalView && { maxWidth: pxToRem(HORIZONTAL_IMAGE_MAX_WIDTH) })}
    >
      {/* Overlay badges and tags */}
      <Flex
        gap={pxToRem(4)}
        top={pxToRem(4)}
        left={pxToRem(4)}
        right={pxToRem(4)}
        zIndex={1}
        flexWrap="wrap"
        position="absolute"
        justifyContent="space-between"
      >
        {/* Index badge */}
        {showIndex !== undefined && (
          <Box
            width={pxToRem(32)}
            height={pxToRem(32)}
            columnGap={pxToRem(2)}
            display="flex"
            borderRadius="50%"
            alignItems="center"
            justifyContent="center"
            backdropFilter={`blur(${pxToRem(4)})`}
            backgroundColor="rgba(247, 247, 247, 0.6)"
          >
            <TextV2
              color="mono14"
              fontWeight={500}
              fontFamily="Inter UI"
              typographyVariant="label3"
            >
              {showIndex}
            </TextV2>
          </Box>
        )}
        {/* Tags overlay (reservation count, discount badge, etc) */}
        {renderTagsOverlay && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
          >
            {renderTagsOverlay()}
          </Box>
        )}
      </Flex>

      {/* Photo carousel */}
      <Box
        width="100%"
        aspectRatio={enableHorizontalView ? HORIZONTAL_ASPECT_RATIO : VERTICAL_ASPECT_RATIO}
        overflow="hidden"
        position="relative"
        height="100%"
        maxHeight={enableHorizontalView ? pxToRem(HORIZONTAL_IMAGE_MAX_HEIGHT) : pxToRem(VERTICAL_IMAGE_MAX_HEIGHT)}
        backgroundColor="mono2"
      >
        <PhotoCarousel
          aspectRatio={enableHorizontalView ? HORIZONTAL_ASPECT_RATIO : VERTICAL_ASPECT_RATIO}
          images={photos}
          navigation={{ variant: 'minimal', visible: 'default' }}
        />
      </Box>
    </Box>
  );
};

// Skeleton component for HotelTilePhotos
interface HotelTilePhotoSkeletonProps
  extends Readonly<{
    enableHorizontalView?: boolean;
  }> {}

export const HotelTilePhotoSkeleton: React.FC<HotelTilePhotoSkeletonProps> = ({ enableHorizontalView = false }) => {
  return (
    <SkeletonThumbnail
      height={enableHorizontalView ? pxToRem(216) : pxToRem(206)}
      overrides={{
        PlaceholderRoot: {
          props: {
            width: enableHorizontalView ? pxToRem(384) : '100%',
            backgroundColor: 'white',
          },
        },
      }}
    />
  );
};
